// WARNING: This file is for internal use only by TenantManager.server.ts and TenantManager.client.ts.
// Do NOT import or use this file directly elsewhere in your codebase.

import { SupabaseClient } from "@supabase/supabase-js";
import { getCurrentUser } from "@/lib/auth/AuthManager.shared";
import { TenantRow, TenantContextResult, TenantLimitsRow } from "@/lib/types";

export interface StorageLimits {
  file_upload_limit_mb: number | null;
  storage_limit_gb: number | null;
  member_limit: number | null;
  allow_bulk_invites: boolean | null;
  allow_multiple_admins: boolean | null;
  allow_audit_logs: boolean | null;
  enable_events?: boolean | null;
  enable_compliance_review?: boolean | null;
  enable_dues_management?: boolean | null;
}

export interface StorageCheckResult {
  allowed: boolean;
  limit?: number;
  current?: number;
  additional?: number;
}

export class TenantManagerShared {
  /**
   * Extract tenant slug from hostname
   */
  static extractTenantSlug(host: string): string | null {
    if (!host) return null;

    // In development mode, assume localhost should use "local-hoa" tenant
    if (host.includes("localhost") && process.env.NODE_ENV === "development") {
      return "local-hoa";
    }

    // Return null for localhost in production (security)
    if (host.includes("localhost")) return null;

    const parts = host.split(".");
    if (parts.length < 2) return null;
    // e.g., sunstatehoa.com
    return parts[0]; // e.g., 'country-manor'
  }

  /**
   * Load complete tenant context with user and role
   */
  static async loadTenantContext(
    authClient: SupabaseClient,
    serviceClient: SupabaseClient,
    host: string
  ): Promise<TenantContextResult> {
    const slug = TenantManagerShared.extractTenantSlug(host);

    if (!slug) {
      return {
        tenant: null,
        user: null,
        hasAdminPrivileges: false,
        memberType: null,
        error: "Invalid tenant slug",
      };
    }

    // Get the currently authenticated user
    const user = await getCurrentUser(authClient);

    // Get the tenant by slug
    const { data: tenant, error: tenantError } = await serviceClient
      .from("tenants")
      .select(
        `
        *,
        subscription:subscriptions(
          status,
          current_period_end
        )
      `
      )
      .eq("slug", slug)
      .single<TenantRow>();

    if (!tenant || tenantError) {
      return {
        tenant: null,
        user,
        hasAdminPrivileges: false,
        memberType: null,
        error: "Unable to load tenant",
      };
    }

    // If signed in, fetch user's capabilities in this tenant
    let hasAdminPrivileges = false;
    let memberType:
      | "owner"
      | "board-member"
      | "renter"
      | "property-manager"
      | "portal-admin"
      | null = null;

    if (user) {
      const { data: membership } = await serviceClient
        .from("memberships")
        .select("has_admin_privileges, member_type")
        .eq("tenant_id", tenant.id)
        .eq("user_id", user.id)
        .maybeSingle();

      // Only assign role if user has a membership in this tenant
      if (membership) {
        hasAdminPrivileges = membership.has_admin_privileges;
        memberType = membership.member_type || null;
      } else {
        // User is authenticated but not a member of this tenant
        // Return error to prevent access
        return {
          tenant: null,
          user: null,
          hasAdminPrivileges: false,
          memberType: null,
          error:
            "You have not been invited to this community. Please contact an administrator for access.",
        };
      }

      // Debug logging removed to reduce noisy console output in dev
    }

    // Check for impersonation state
    let impersonationInfo = undefined;
    if (user) {
      try {
        // Import ImpersonationManager dynamically to avoid circular dependencies
        // Use client version for browser compatibility
        const { ImpersonationManagerClient } = await import(
          "../auth/ImpersonationManager.client"
        );
        // Provide a minimal member manager adapter used only for impersonation checks
        const memberManagerAdapter = {
          async checkUserIsAdmin(
            userId: string,
            tenantId: string
          ): Promise<boolean> {
            const { data } = await serviceClient
              .from("memberships")
              .select("has_admin_privileges")
              .eq("tenant_id", tenant.id)
              .eq("user_id", userId)
              .maybeSingle();
            return !!data?.has_admin_privileges;
          },
          async getUserMembershipRole(
            userId: string,
            tenantId: string
          ): Promise<"admin" | "member" | null> {
            const { data } = await serviceClient
              .from("memberships")
              .select("has_admin_privileges")
              .eq("tenant_id", tenant.id)
              .eq("user_id", userId)
              .maybeSingle();
            if (!data) return null;
            return data.has_admin_privileges ? "admin" : "member";
          },
          async getUserMembershipType(
            userId: string,
            tenantId: string
          ): Promise<
            | "owner"
            | "board-member"
            | "renter"
            | "property-manager"
            | "portal-admin"
            | null
          > {
            const { data } = await serviceClient
              .from("memberships")
              .select("member_type")
              .eq("tenant_id", tenant.id)
              .eq("user_id", userId)
              .maybeSingle();
            return (data?.member_type as any) || null;
          },
        } as any;

        const impersonationManager = new ImpersonationManagerClient(
          serviceClient,
          { getCurrentUser: () => getCurrentUser(authClient) } as any,
          memberManagerAdapter
        );

        const effectiveRole = await impersonationManager.getEffectiveRole(
          user.id,
          tenant.id
        );

        if (effectiveRole.isImpersonating) {
          impersonationInfo = {
            isImpersonating: true,
            actualMemberType: effectiveRole.actualMemberType,
            effectiveMemberType: effectiveRole.effectiveMemberType,
            impersonatedBy: effectiveRole.impersonatedBy,
            impersonatedAt: effectiveRole.impersonatedAt,
          };

          // During impersonation, always drop admin privileges and honor the effective member type
          hasAdminPrivileges = false;
          memberType = effectiveRole.effectiveMemberType as any;
        }
      } catch (error) {
        // Avoid spamming logs if impersonation cannot be checked
        if (process.env.NODE_ENV === "development") {
          console.warn("Failed to check impersonation state:", error);
        }
        // Continue without impersonation if there's an error
      }
    }

    return {
      tenant,
      user,
      hasAdminPrivileges,
      memberType,
      error: null,
      impersonationInfo,
    };
  }

  /**
   * Check if a user can invite members to this tenant
   */
  static canInviteMembers(
    tenant: TenantRow,
    userEmail: string | null
  ): boolean {
    return true;
  }

  /**
   * Check if a feature is enabled for a tenant
   */
  static async isFeatureEnabled(
    client: SupabaseClient,
    tenantId: string,
    feature: "enable_events" | "enable_compliance_review" | "enable_dues_management"
  ): Promise<boolean> {
    try {
      // First try to get tenant-specific limits from tenant_limits table
      const { data: tenantLimits } = await client
        .from("tenant_limits")
        .select("enable_events, enable_compliance_review, enable_dues_management")
        .eq("tenant_id", tenantId)
        .single();

      if (tenantLimits && tenantLimits[feature] !== null) {
        return tenantLimits[feature];
      }

      // Fall back to effective tenant limits view
      const { data: effectiveLimits } = await client
        .from("effective_tenant_limits")
        .select("enable_events, enable_compliance_review, enable_dues_management")
        .eq("tenant_id", tenantId)
        .single();

      return effectiveLimits?.[feature] || false;
    } catch (error) {
      console.error(`Error checking feature ${feature} for tenant ${tenantId}:`, error);
      return false;
    }
  }

  /**
   * Get tenant limits from effective_tenant_limits view
   */
  static async getTenantLimits(
    client: SupabaseClient,
    tenantId: string
  ): Promise<StorageLimits> {
    const { data, error } = await client
      .from("effective_tenant_limits")
      .select(
        `
        file_upload_limit_mb,
        storage_limit_gb,
        member_limit,
        allow_bulk_invites,
        allow_multiple_admins,
        allow_audit_logs,
        enable_events,
        enable_compliance_review,
        enable_dues_management
      `
      )
      .eq("tenant_id", tenantId)
      .single();

    if (error || !data) {
      // Default limits if not found - these should match the Free plan
      return {
        file_upload_limit_mb: 50, // 50MB default (Free plan)
        storage_limit_gb: 1, // 1GB default (Free plan)
        member_limit: 1, // One member per tenant (Free plan)
        allow_bulk_invites: false, // Free plan doesn't allow bulk invites
        allow_multiple_admins: false, // Free plan doesn't allow multiple admins
        allow_audit_logs: false, // Free plan doesn't include audit logs
        enable_events: false, // Free plan doesn't include events
        enable_compliance_review: false, // Free plan doesn't include compliance
        enable_dues_management: false, // Free plan doesn't include dues
      };
    }

    return {
      file_upload_limit_mb: data.file_upload_limit_mb,
      storage_limit_gb: data.storage_limit_gb,
      member_limit: data.member_limit,
      allow_bulk_invites: data.allow_bulk_invites,
      allow_multiple_admins: data.allow_multiple_admins,
      allow_audit_logs: data.allow_audit_logs,
      enable_events: data.enable_events,
      enable_compliance_review: data.enable_compliance_review,
      enable_dues_management: data.enable_dues_management,
    };
  }

  /**
   * Check if a file size is within tenant limits
   */
  static async checkFileSizeLimit(
    client: SupabaseClient,
    tenantId: string,
    fileSizeBytes: number
  ): Promise<{ allowed: boolean; limit?: number; current?: number }> {
    const limits = await TenantManagerShared.getTenantLimits(client, tenantId);
    const fileSizeMB = fileSizeBytes / (1024 * 1024);

    if (!limits.file_upload_limit_mb) {
      return { allowed: true }; // No limit set
    }

    return {
      allowed: fileSizeMB <= limits.file_upload_limit_mb,
      limit: limits.file_upload_limit_mb,
      current: fileSizeMB,
    };
  }

  /**
   * Check if adding a file would exceed storage limits
   */
  static async checkStorageLimit(
    client: SupabaseClient,
    tenantId: string,
    additionalSizeBytes: number = 0
  ): Promise<StorageCheckResult> {
    const limits = await TenantManagerShared.getTenantLimits(client, tenantId);

    if (!limits.storage_limit_gb) {
      return { allowed: true }; // No limit set
    }

    // Get current storage usage from database
    const { data: usageData, error } = await client
      .from("documents")
      .select("size_bytes")
      .eq("tenant_id", tenantId)
      .not("size_bytes", "is", null);

    if (error) {
      console.error("Failed to get storage usage:", error);
      return { allowed: false, limit: limits.storage_limit_gb };
    }

    // Calculate current usage in bytes
    const currentUsageBytes =
      usageData?.reduce((sum, doc) => sum + (doc.size_bytes || 0), 0) || 0;

    const currentUsageGB = currentUsageBytes / (1024 * 1024 * 1024);
    const additionalSizeGB = additionalSizeBytes / (1024 * 1024 * 1024);
    const totalUsageGB = currentUsageGB + additionalSizeGB;

    return {
      allowed: totalUsageGB <= limits.storage_limit_gb,
      limit: limits.storage_limit_gb,
      current: currentUsageGB,
      additional: additionalSizeGB,
    };
  }

  /**
   * Check if tenant can have multiple admins
   */
  static async canHaveMultipleAdmins(
    client: SupabaseClient,
    tenantId: string
  ): Promise<boolean> {
    const limits = await TenantManagerShared.getTenantLimits(client, tenantId);
    return limits.allow_multiple_admins || false;
  }

  /**
   * Check if tenant can use bulk invites
   */
  static async canUseBulkInvites(
    client: SupabaseClient,
    tenantId: string
  ): Promise<boolean> {
    const limits = await TenantManagerShared.getTenantLimits(client, tenantId);
    return limits.allow_bulk_invites || false;
  }

  /**
   * Check if tenant has audit logs enabled
   */
  static async hasAuditLogs(
    client: SupabaseClient,
    tenantId: string
  ): Promise<boolean> {
    const limits = await TenantManagerShared.getTenantLimits(client, tenantId);
    return limits.allow_audit_logs || false;
  }

  /**
   * Get member limit for tenant
   */
  static async getMemberLimit(
    client: SupabaseClient,
    tenantId: string
  ): Promise<number | null> {
    const limits = await TenantManagerShared.getTenantLimits(client, tenantId);
    return limits.member_limit;
  }

  static async updateTenant(
    client: SupabaseClient,
    tenantId: string,
    {
      name,
      description,
      logoUrl,
      heroImageUrl,
      timezone,
    }: {
      name: string;
      description?: string;
      logoUrl?: string | null;
      heroImageUrl?: string | null;
      timezone?: string | null;
    }
  ): Promise<boolean> {
    console.log("TenantManager.updateTenant called with:", {
      tenantId,
      name,
      description,
      logoUrl,
      heroImageUrl,
      timezone,
    });

    const updateData: any = {
      name,
      description,
      logo_url: logoUrl,
      background_image_url: heroImageUrl,
    };

    if (timezone !== undefined) {
      updateData.timezone_code = timezone;
    }

    const { error } = await client
      .from("tenants")
      .update(updateData)
      .eq("id", tenantId);

    if (error) {
      console.error("Database error in updateTenant:", error);
    }

    return !error;
  }
}
